/**
 * Light theme color palette
 * Raw color definitions with 0-100 scale for each color
 */

const lightColorPalette = {
  // Primary colors
  blue: {
    0: '#eff6ff',
    10: '#dbeafe',
    20: '#bfdbfe',
    30: '#93c5fd',
    40: '#60a5fa',
    50: '#3b82f6',
    60: '#2563eb',
    70: '#1d4ed8',
    80: '#1e40af',
    90: '#1e3a8a',
    100: '#172554',
  },

  // Secondary colors
  green: {
    0: '#f0fdf4',
    10: '#dcfce7',
    20: '#bbf7d0',
    30: '#86efac',
    40: '#4ade80',
    50: '#22c55e',
    60: '#16a34a',
    70: '#15803d',
    80: '#166534',
    90: '#14532d',
    100: '#052e16',
  },

  // Neutral colors
  gray: {
    0: '#f9fafb',
    10: '#f3f4f6',
    20: '#e5e7eb',
    30: '#d1d5db',
    40: '#9ca3af',
    50: '#6b7280',
    60: '#4b5563',
    70: '#374151',
    80: '#1f2937',
    90: '#111827',
    100: '#030712',
  },

  // Accent colors
  red: {
    0: '#fef2f2',
    10: '#fee2e2',
    20: '#fecaca',
    30: '#fca5a5',
    40: '#f87171',
    50: '#ef4444',
    60: '#dc2626',
    70: '#b91c1c',
    80: '#991b1b',
    90: '#7f1d1d',
    100: '#450a0a',
  },

  yellow: {
    0: '#fffbeb',
    10: '#fef3c7',
    20: '#fde68a',
    30: '#fcd34d',
    40: '#fbbf24',
    50: '#f59e0b',
    60: '#d97706',
    70: '#b45309',
    80: '#92400e',
    90: '#78350f',
    100: '#451a03',
  },

  purple: {
    0: '#faf5ff',
    10: '#f3e8ff',
    20: '#e9d5ff',
    30: '#d8b4fe',
    40: '#c084fc',
    50: '#a855f7',
    60: '#9333ea',
    70: '#7c3aed',
    80: '#6b21a8',
    90: '#581c87',
    100: '#3b0764',
  },

  pink: {
    0: '#fdf2f8',
    10: '#fce7f3',
    20: '#fbcfe8',
    30: '#f9a8d4',
    40: '#f472b6',
    50: '#ec4899',
    60: '#db2777',
    70: '#be185d',
    80: '#9d174d',
    90: '#831843',
    100: '#500724',
  },

  indigo: {
    0: '#eef2ff',
    10: '#e0e7ff',
    20: '#c7d2fe',
    30: '#a5b4fc',
    40: '#818cf8',
    50: '#6366f1',
    60: '#4f46e5',
    70: '#4338ca',
    80: '#3730a3',
    90: '#312e81',
    100: '#1e1b4b',
  },

  teal: {
    0: '#f0fdfa',
    10: '#ccfbf1',
    20: '#99f6e4',
    30: '#5eead4',
    40: '#2dd4bf',
    50: '#14b8a6',
    60: '#0d9488',
    70: '#0f766e',
    80: '#115e59',
    90: '#134e4a',
    100: '#042f2e',
  },

  orange: {
    0: '#fff7ed',
    10: '#ffedd5',
    20: '#fed7aa',
    30: '#fdba74',
    40: '#fb923c',
    50: '#f97316',
    60: '#ea580c',
    70: '#dc2626',
    80: '#c2410c',
    90: '#9a3412',
    100: '#7c2d12',
  },

  // Semantic colors (aliases)
  primary: {
    0: '#eff6ff',
    10: '#dbeafe',
    20: '#bfdbfe',
    30: '#93c5fd',
    40: '#60a5fa',
    50: '#3b82f6',
    60: '#2563eb',
    70: '#1d4ed8',
    80: '#1e40af',
    90: '#1e3a8a',
    100: '#172554',
  },

  secondary: {
    0: '#f0fdf4',
    10: '#dcfce7',
    20: '#bbf7d0',
    30: '#86efac',
    40: '#4ade80',
    50: '#22c55e',
    60: '#16a34a',
    70: '#15803d',
    80: '#166534',
    90: '#14532d',
    100: '#052e16',
  },

  success: {
    0: '#f0fdf4',
    10: '#dcfce7',
    20: '#bbf7d0',
    30: '#86efac',
    40: '#4ade80',
    50: '#22c55e',
    60: '#16a34a',
    70: '#15803d',
    80: '#166534',
    90: '#14532d',
    100: '#052e16',
  },

  warning: {
    0: '#fffbe6',
    10: '#fff4a3',
    20: '#ffeb66',
    30: '#ffe033',
    40: '#f0c800',
    50: '#d4af00',
    60: '#b89600',
    70: '#997d00',
    80: '#7a6400',
    90: '#5c4b00',
    100: '#3d3200',
  },

  error: {
    0: '#fef2f2',
    10: '#fee2e2',
    20: '#fecaca',
    30: '#fca5a5',
    40: '#f87171',
    50: '#ef4444',
    60: '#dc2626',
    70: '#b91c1c',
    80: '#991b1b',
    90: '#7f1d1d',
    100: '#450a0a',
  },

  info: {
    0: '#eff6ff',
    10: '#dbeafe',
    20: '#bfdbfe',
    30: '#93c5fd',
    40: '#60a5fa',
    50: '#3b82f6',
    60: '#2563eb',
    70: '#1d4ed8',
    80: '#1e40af',
    90: '#1e3a8a',
    100: '#172554',
  },

  // Paper-specific colors
  paper: {
    0: '#ffffff',
    10: '#fefefe',
    20: '#fdfdfd',
    30: '#fcfcfc',
    40: '#f9f9f9',
    50: '#f5f5f5',
    60: '#ececec',
    70: '#e0e0e0',
    80: '#d0d0d0',
    90: '#b8b8b8',
    100: '#999999',
  },

  // Sidebar-specific colors
  sidebar: {
    0: '#f5f5f5',
    10: '#dddddd',
    20: '#d1d5db',
    30: '#9ca3af',
    40: '#6b7280',
    50: '#4b5563',
    60: '#374151',
    70: '#1f2937',
    80: '#111827',
    90: '#797979',
    100: '#030712',
  },

  // Tooltip-specific colors
  tooltip: {
    0: '#ffffff',
    10: '#f5f5f5',
    20: '#e0e0e0',
    30: '#d1d5db',
    40: '#9ca3af',
    50: '#6b7280',
    60: '#4b5563',
    70: '#374151',
    80: '#2d2d2d',
    90: '#1f2937',
    100: '#000000',
  },

  // Note-specific colors
  noteColors: {
    0: '#fef3c7',
    10: '#dbeafe',
    20: '#d1fae5',
    30: '#fce7f3',
    40: '#e9d5ff',
    50: '#fed7aa',
    60: '#cffafe',
    70: '#f3f4f6',
    80: '#fef2f2',
    90: '#ecfdf5',
    100: '#f0f9ff',
    110: '#faf5ff',
  },
};

export default lightColorPalette;
